/**
 * DevAuthProvider — replaces Auth0/Parent auth in agentic/local-dev mode.
 *
 * Activated when `import.meta.env.VITE_AGENTIC === "true"`. When active, it:
 * - Bypasses Auth0 + Parent auth entirely (no redirects, no JWKS, no
 *   token refresh).
 * - Decodes `VITE_DEV_TOKEN` (format `dev-<base64json>`) to populate a mock
 *   user identity in our internal `AuthContext`.
 * - Installs a `window.Auth0.getAccessTokenSilently` shim that returns the dev
 *   token verbatim, so `baseQuerySl2.prepareAuthHeaders()` transparently picks
 *   it up.
 * - Renders children synchronously — no loading state, the mock user is
 *   available immediately.
 *
 * The dev token format mirrors the CRM `DevAuth0Provider` pattern: a clear
 * `dev-<base64json>` envelope that the local-lambda authorizer recognizes
 * without doing real JWT validation. This file is the SL2 sibling.
 *
 * Production safety: this module is only mounted from `main.tsx` when the
 * env flag is true. Production builds never have `VITE_AGENTIC=true`, so the
 * `window.Auth0` shim is never installed in real use.
 */
import { FC, PropsWithChildren, useMemo } from "react"
import { AuthContext } from "./AuthContext"
import { AuthStrategyProvider } from "./AuthStrategyContext"

const DEV_TOKEN = import.meta.env.VITE_DEV_TOKEN || ""

function parseDevTokenPayload(): Record<string, any> {
  try {
    if (!DEV_TOKEN.startsWith("dev-")) return {}
    return JSON.parse(atob(DEV_TOKEN.slice(4)))
  } catch {
    return {}
  }
}

const payload = parseDevTokenPayload()

// Install the Auth0 window shim so prepareAuthHeaders in baseQuerySl2
// transparently picks up the dev token. The shim simply returns the dev token.
function installWindowTokenShims() {
  ;(window as any).Auth0 = {
    getAccessTokenSilently: async () => DEV_TOKEN,
  }
}

// Install eagerly at module load — must be in place before any
// RTK Query call fires from a child component.
installWindowTokenShims()

export const DevAuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const ctxValue = useMemo(
    () => ({
      isAuthenticated: true,
      isProcessing: false,
      isProcessed: true,
      isReady: true,
    }),
    [],
  )

  // We pretend to be the auth0 strategy so any code reading
  // useAuthStrategy() gets a stable value (baseQuerySl2 also reads this).
  return (
    <AuthStrategyProvider strategy="auth0">
      <AuthContext.Provider value={ctxValue}>{children}</AuthContext.Provider>
    </AuthStrategyProvider>
  )
}

// Expose the parsed dev-token payload for diagnostics / test harnesses.
export const devUser = {
  sub: payload.sub || "dev-user",
  email: payload.email || "dev@localhost",
  roles: payload.roles || [],
}
